/** @jsx h */
/** @jsxFrag Fragment */
import { h, Fragment } from "preact";

import preload from "./utils/preload";

import Ajv, { ValidateFunction } from "ajv";
import addFormats from "ajv-formats";
import { VNode, JSX } from "preact";
import { useCallback, useEffect, useMemo, useRef } from "preact/hooks";

import "./style.css";
import useThunkReducer from "./utils/thunkReducer";
import {
  FormState,
  Form as FormData,
  FormPage,
  BasicQuestionBlock,
  FieldRecord,
  FormErrorMsg,
  FormTypes,
  QuestionBlockParams,
} from "./FormTypes";
import { FormAction, formReducer } from "./formReducer";
import { BodyPage as BodyPageComponent } from "./BodyPage";
import {
  ArchivedIndicator,
  FormErrorNotice,
  PageFooter,
  PreviewIndicator,
} from "./components/common";
import Privacy from "./components/Privacy";
import { isContentBlock, isQuestionBlock } from "./utils/typePredicates";
import { buildFieldsReducerCallback } from "./utils/formFields";
import {
  buildFormSchema,
  formDefinitionIsValid,
  getSubmissionData,
  validateResponses,
  validateURLParams,
} from "./utils/validation";
import { submitForm, uploadFiles } from "./ajax";
import { getVisiblePages } from "./utils/visiblePages";
import { FormProvider } from "./formContext";

const getFormInstance = () => {
  const instance = window.location.hash.split("?")[0].replace("#", "");

  return instance === "" ? null : instance;
};

const buildInitialFormState = (
  pages: FormPage[],
  formId: string,
  userAgent: string,
  formInstance: string | null,
  livePreview: boolean = false
): FormState => {
  const questions: BasicQuestionBlock[] = pages.flatMap((p, idx) =>
    p.blocks.filter(isQuestionBlock).map((b) => ({
      ...b,
      pageIdx: idx,
    }))
  );
  const fields: FieldRecord = questions.reduce(buildFieldsReducerCallback, {});
  const visiblePages = getVisiblePages(pages, fields);
  const formDefinitionIsValid = true;

  const result: FormState = {
    formState: { status: "pending" as const },
    currentPage: pages[0],
    pages,
    visiblePages,
    fields,
    formId,
    userAgent,
    submitDisabled: false,
    formDefinitionIsValid,
    livePreview,
    urlParamErrors: {},
  };

  if (formInstance !== null) {
    result["formInstance"] = formInstance;
  }

  return result;
};

// Utility function to condense the prefill_fields and url_mappings into a single object, for backwards compatibility
const transformPrefillFields = (
  urlMappings: Record<string, string> = {},
  prefillFields: string[] = []
) =>
  prefillFields.reduce((acc: Record<string, string>, f: string) => {
    return {
      ...acc,
      [f]: f,
    };
  }, urlMappings);

// Populate the form state with prefill data from the URL
const formStateWithPrefillFields = (
  state: FormState,
  urlParams: URLSearchParams,
  _form_type?: FormTypes,
  urlMappings = {} as Record<string, string>
): FormState => {
  const fields = state.fields;

  // handle url prefill params
  if (!state.livePreview) {
    Object.values(fields).forEach((field) => {
      if (!field.writeToAttribute) {
        return;
      }
      // Check if this field is expected to be pre-filled
      const fieldUrlParam = urlMappings[field.writeToAttribute];

      if (!!fieldUrlParam) {
        field.isPrefilled = true;

        if (urlParams.has(fieldUrlParam)) {
          let parameterValue;

          if (["date", "date_of_birth"].includes(field.block_type)) {
            parameterValue = new Date(urlParams.get(fieldUrlParam) as string);
          } else {
            parameterValue = urlParams.get(fieldUrlParam);
          }

          field.currentValue = parameterValue;
          // set status to "input" to ensure this field gets validated.
          field.status = "input";
        }
      }
    });
  } else {
    Object.values(fields).forEach((field) => {
      // Mark prefill fields
      if (field.params?.url_name) {
        field.isPrefilled = true;
      }
    });
  }

  return {
    ...state,
    fields,
  };
};

// post message so an event listener can be added when form is iframed
const notifyParentFormSubmitted = () => {
  window.parent?.postMessage("lexer-form-submitted", "*");
};

const promptErrorMessage = (messageKey: string) => {
  if (FormErrorMsg[messageKey as keyof typeof FormErrorMsg]) {
    return FormErrorMsg[messageKey as keyof typeof FormErrorMsg];
  } else {
    return FormErrorMsg.UNKNOWN_ERROR;
  }
};

const submitFormIfValid = () => {
  return async (
    dispatch: (action: FormAction) => void,
    getState: () => FormState
  ) => {
    try {
      const state = getState();

      if (state.formState.status !== "invalid") {
        dispatch({
          type: "updateWorkflowState",
          state: { status: "submitting" },
        });

        const formData = getSubmissionData(state, true);

        // upload files
        await uploadFiles(state.fileBlobs);

        const response = await submitForm(formData);

        if (response.ok === true) {
          dispatch({
            type: "updateWorkflowState",
            state: { status: "complete" },
          });
          notifyParentFormSubmitted();
        } else {
          // Server errors
          const text = await response.text();
          try {
            const data = JSON.parse(text); // Try to parse the response as JSON

            // The response is a JSON object
            // JSON handling
            if (data.error) {
              console.error("Form submission failed with error: ", data.error);
              dispatch({
                type: "updateWorkflowState",
                state: {
                  status: "failed",
                  error: promptErrorMessage(data.error),
                },
              });
            } else {
              console.error("Form submission failed with error: ", data);
              dispatch({
                type: "updateWorkflowState",
                state: {
                  status: "failed",
                  error: FormErrorMsg.UNKNOWN_ERROR,
                },
              });
            }
          } catch (err) {
            // The response wasn't a JSON object
            // Text handling
            console.error("Form submission failed with error: ", text);
            dispatch({
              type: "updateWorkflowState",
              state: {
                status: "failed",
                error: FormErrorMsg.UNKNOWN_ERROR,
              },
            });
          }
        }

        dispatch({
          type: "setPage",
          page: state.pages[state.pages.length - 1],
        });
      }
    } catch (err: any) {
      // Client errors
      if (err instanceof Error && err.message === FormErrorMsg.MISSING_TOKENS) {
        console.error(err.message);
        dispatch({
          type: "updateWorkflowState",
          state: { status: "failed", error: err.message },
        });
      } else {
        console.error("Unknown error:", err);
        dispatch({
          type: "updateWorkflowState",
          state: {
            status: "failed",
            error: FormErrorMsg.SUBMISSION_FAILED,
          },
        });
      }
    } finally {
      dispatch({ type: "setSubmitDisabled", value: false });
    }
  };
};

const nextPageIfValid = (nextPage: FormPage) => {
  return (
    dispatch: (action: FormAction) => void,
    getState: () => FormState
  ) => {
    const { formState } = getState();

    if (formState.status !== "invalid") {
      dispatch({ type: "setPage", page: nextPage });
    }

    dispatch({ type: "setSubmitDisabled", value: false });
  };
};

type AttributeJSONSchema = {
  required: string[];
};

export type AppProps = FormData & {
  schema?: AttributeJSONSchema;
  live_preview?: boolean;
};

export function App(props: AppProps): VNode {
  const {
    token,
    privacy_url,
    privacy_url_label,
    pages,
    live_preview,
    archived,
    form_type,
    prefill_fields,
    url_mappings,
  } = props;

  const urlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  // Filling responses from url parameters used to work differently.
  // On the off chance that we're still receiving the old format, merge it into the new one.
  const finalUrlMappings = useMemo(() => {
    return transformPrefillFields(url_mappings, prefill_fields);
  }, [url_mappings, prefill_fields]);

  const initState = useMemo(() => {
    const state = buildInitialFormState(
      pages,
      token,
      navigator.userAgent,
      getFormInstance(),
      live_preview
    );
    return formStateWithPrefillFields(
      state,
      urlParams,
      form_type,
      finalUrlMappings
    );
    // TODO: there should be a better way to do this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [state, dispatch] = useThunkReducer<FormState, FormAction>(
    formReducer,
    initState
  );

  // Before doing anything else, check that the form definition is valid
  useEffect(() => {
    const isValid = formDefinitionIsValid(props, !!live_preview);
    dispatch({ type: "andFormIsValid", value: isValid });
    if (!isValid) {
      console.error("Form Definition is invalid");
    }
  }, [props, dispatch, live_preview]);

  const validatorRef = useRef<ValidateFunction | null>(null);

  // Compile the schema with AJV, add formatter support, and store the validator on a ref
  if (validatorRef.current === null) {
    const ajv = new Ajv({ coerceTypes: true, allErrors: true });
    addFormats(ajv);
    const schema = buildFormSchema(state.fields);

    validatorRef.current = ajv.compile(schema);
  }

  const handleValidate = useCallback(
    (_?: h.JSX.TargetedFocusEvent<HTMLElement>) => {
      if (validatorRef.current) {
        return dispatch(validateResponses(validatorRef.current));
      } else {
        console.error("No validation function found");
      }
    },
    [dispatch]
  );

  // Validate responses to hidden fields prefilled via URL params.
  // This should generally only run on first load.
  useEffect(() => {
    if (validatorRef.current) {
      // Run validation on initial load to correctly assign block visibility
      dispatch(validateResponses(validatorRef.current));
      // and validate prefilled fields
      if (Object.entries(finalUrlMappings).length > 0) {
        dispatch(validateURLParams(finalUrlMappings));
      }
    }
  }, [dispatch, finalUrlMappings, live_preview]);

  const handleSubmit = useCallback(() => {
    if (state.submitDisabled) {
      // just in case
      return;
    }
    dispatch({ type: "setSubmitDisabled", value: true });
    // requestAnimationFrame queues a task to be executed just *before* the next frame is drawn
    // since we need at least one frame to be drawn to allow time for the submit button to be disabled
    // calling requestAnimationFrame twice will delay execution until just before the *next* frame
    // guaranteeing at least 1 frame being drawn before validation kicks off
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (validatorRef.current) {
          dispatch(validateResponses(validatorRef.current, { scope: "all" }));
          // Don't try to submit data if we're in preview mode
          if (!live_preview) {
            dispatch(submitFormIfValid());
          } else {
            dispatch(nextPageIfValid(state.pages[state.pages.length - 1]));
            notifyParentFormSubmitted();
          }
        } else {
          console.error("No validation function found");
        }
      });
    });
  }, [state, dispatch, live_preview]);

  const handleNextPage = useCallback(
    (currentPageIdx: number, nextPage: FormPage) => {
      if (validatorRef.current) {
        dispatch(
          validateResponses(validatorRef.current, {
            scope: "page",
            pageIdx: currentPageIdx,
          })
        );
        dispatch(nextPageIfValid(nextPage));
      }
    },
    [dispatch]
  );

  const currentPageIdx = state.pages.findIndex(
    (p) => p.id === state.currentPage.id
  );
  const isFirstPage = currentPageIdx === 0;
  const isLastPage = currentPageIdx === state.pages.length - 1;

  if (archived) {
    return (
      <div className="lx-form">
        <div className="lx-form-container">
          <ArchivedIndicator />
        </div>
      </div>
    );
  }

  const requiredTypesPerPage = useMemo(
    () =>
      state.pages.map(
        (p) =>
          new Set(
            p.blocks.map((b) =>
              isContentBlock(b) ? b.block_type : state.fields[b.id].input_type
            )
          )
      ),
    // TODO: there should be a better way to do this
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const nextVisiblePageIdx = state.visiblePages.indexOf(state.currentPage) + 1;
  const nextPageIdx = state.pages.indexOf(
    state.visiblePages[nextVisiblePageIdx]
  );

  // preload required blocks for the next visible page
  useEffect(() => {
    const toPreload = requiredTypesPerPage.at(nextPageIdx);
    if (toPreload) {
      preload([...toPreload]);
    }
  }, [nextPageIdx, requiredTypesPerPage]);

  let content;

  if (!state.formDefinitionIsValid) {
    content = (
      <FormErrorNotice
        title={"We couldn't make sense of this form."}
        description={
          "We couldn't display this form, because some question details were missing or incorrect!"
        }
      />
    );
  } else if (Object.entries(state.urlParamErrors).length > 0) {
    content = (
      <FormErrorNotice
        title="We couldn't make sense of this form."
        description="Some expected values were missing from the form address or were invalid. If you copied the address from elsewhere, please make sure to include all characters."
      />
    );
  } else if (state.formState.status === "failed") {
    content = (
      <FormErrorNotice
        title={"We couldn't submit this form."}
        description={state.formState.error}
      />
    );
  } else {
    content = (
      <>
        <BodyPageComponent
          dispatch={dispatch}
          handleValidate={handleValidate}
          fields={state.fields}
          currentPage={state.currentPage}
          pages={state.visiblePages}
          isHeadingPage={isFirstPage || isLastPage}
        />
        {state.formDefinitionIsValid && !isLastPage && (
          <PageFooter
            dispatch={dispatch}
            pages={state.visiblePages}
            currentPageId={state.currentPage.id}
            handleSubmit={handleSubmit}
            handleNextPage={handleNextPage}
            submitDisabled={!!state.submitDisabled}
          />
        )}
      </>
    );
  }

  return (
    <FormProvider form={props}>
      <div className="lx-form">
        <div className="lx-form-container">
          {live_preview && <PreviewIndicator />}
          {content}
        </div>
        {isFirstPage && privacy_url && privacy_url_label && (
          <Privacy
            privacyUrl={privacy_url}
            privacyUrlLabel={privacy_url_label}
          />
        )}
      </div>
    </FormProvider>
  );
}
